import React from 'react';

import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Modal,
  Typography
} from '@mui/material';

import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

import LoadingButton from '../../../../components/buttons/Button';
import { TILE_ACCENT } from '../../../../components/utils/SummaryTiles';

import style from "../../../../global.scss";
import "../../../../components/modals/styles.scss";

const LABEL_COLOR = 'rgba(226, 242, 249, 0.6)';

// A fingerprint tells you two templates differ; this tells you where. Only the
// fields that actually take part in a sync are compared, so a row highlighted
// here is a row that will change when you transfer.
const FIELDS = [
  { key: 'name', label: 'Name' },
  { key: 'subject', label: 'Subject' },
  { key: 'tag', label: 'Tag' },
  { key: 'important', label: 'Important', render: (v) => (v ? "yes" : "no") },
];

const Side = ({ children }) => (
  <Box flex={1} minWidth={0}>{children}</Box>
);

const Body = ({ template, empty }) => (
  <Box
    sx={{
      background: "rgba(0, 0, 0, 0.35)",
      border: "1px solid #2c2d2f",
      borderRadius: "8px",
      padding: "10px 12px",
      maxHeight: "38vh",
      overflow: "auto",
    }}>
    <Typography
      component='pre'
      variant='caption'
      sx={{
        fontFamily: "monospace",
        whiteSpace: "pre-wrap",
        wordBreak: "break-word",
        margin: 0,
        color: template ? "inherit" : LABEL_COLOR,
      }}>
      {template ? (template.template || "(empty body)") : empty}
    </Typography>
  </Box>
);

const TemplateDiffModal = ({
  open,
  setOpen,
  loading,
  detail,
  targetLabel,
  onPush,
  onPull,
  pushing,
  pulling,
}) => {
  const handleClose = () => setOpen(false);

  const local = detail?.source?.template || null;
  const remote = detail?.target?.template || null;
  const remoteError = detail?.target?.error;

  const differs = (key) => {
    if (!local || !remote) return false;
    return String(local[key] ?? "") !== String(remote[key] ?? "");
  };

  const bodyDiffers = !!local && !!remote && local.content_hash !== remote.content_hash;

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="template-diff-title"
      aria-describedby="template-diff-description">
      {/* `.modal` is width:100%, so the width is set on a wrapper rather than
          fought with on the element itself */}
      <Box className="flexCenterCenterRow" width={"100vw"} height={"100vh"}>
        <Box width={"min(1100px, 92vw)"}>
        <Box className="modal">

          <Box sx={{ margin: "15px 15px 10px 15px" }}>
            <Typography variant='h6' sx={{ color: style["primary"] }}>
              {detail?.code || "Template"}
            </Typography>
            <Typography variant='caption' sx={{ color: LABEL_COLOR }}>
              Comparing this environment against {targetLabel}. Nothing changes until
              you push or pull.
            </Typography>
          </Box>

          <Divider sx={{ backgroundColor: "rgba(44, 45, 47, 1)" }} />

          {
            loading
            ?
            <Box height={"320px"} className="flexCenterCenterRow">
              <CircularProgress />
            </Box>
            :
            <Box sx={{ padding: "16px", maxHeight: "70vh", overflow: "auto" }}>

              {/* ---------- column headers ---------- */}
              <Box className='flexCenter_Row' gap={"16px"} mb={"12px"}>
                <Side>
                  <Typography variant='subtitle2'>
                    Here — {detail?.source?.environment || "this environment"}
                  </Typography>
                </Side>
                <Side>
                  <Typography variant='subtitle2'>{targetLabel}</Typography>
                </Side>
              </Box>

              {/* ---------- field-by-field ---------- */}
              {
                FIELDS.map((field) => {
                  const changed = differs(field.key);
                  const show = (t) => {
                    if (!t) return <Typography variant='caption' sx={{ color: LABEL_COLOR }}>—</Typography>;
                    const value = field.render ? field.render(t[field.key]) : (t[field.key] || "");
                    return (
                      <Typography variant='body2' sx={{ wordBreak: "break-word" }}>
                        {value || <span style={{ color: LABEL_COLOR }}>(empty)</span>}
                      </Typography>
                    );
                  };
                  return (
                    <Box
                      key={field.key}
                      sx={{
                        borderLeft: `3px solid ${changed ? TILE_ACCENT.bad : 'transparent'}`,
                        background: changed ? `${TILE_ACCENT.bad}12` : "transparent",
                        borderRadius: "6px",
                        padding: "6px 10px",
                        mb: "4px",
                      }}>
                      <Typography variant='caption' sx={{ color: LABEL_COLOR, display: "block" }}>
                        {field.label}{changed ? " · differs" : ""}
                      </Typography>
                      <Box className='flexCenter_Row' gap={"16px"}>
                        <Side>{show(local)}</Side>
                        <Side>{show(remote)}</Side>
                      </Box>
                    </Box>
                  );
                })
              }

              {/* ---------- bodies ---------- */}
              <Box className='flexCenter_Row' gap={"10px"} mt={"14px"} mb={"8px"}>
                <Typography variant='subtitle2'>Body</Typography>
                {
                  local && remote
                  &&
                  <Chip
                    size='small'
                    variant='filled'
                    color={bodyDiffers ? 'error' : 'success'}
                    label={bodyDiffers ? "Content differs" : "Identical"} />
                }
              </Box>

              <Box display={"flex"} alignItems={"flex-start"} gap={"16px"}>
                <Side>
                  <Body template={local} empty={"Not present in this environment"} />
                </Side>
                <Side>
                  <Body
                    template={remote}
                    empty={remoteError ? `Could not read the target — ${remoteError}` : `Not present on ${targetLabel}`} />
                </Side>
              </Box>
            </Box>
          }

          <Divider sx={{ backgroundColor: "rgba(44, 45, 47, 1)" }} />

          <Box className="flexCenterSBRow" sx={{ gap: "20px", margin: "15px" }}>
            <Typography variant='caption' sx={{ color: LABEL_COLOR }}>
              A transfer overwrites the destination copy and never deletes anything.
            </Typography>
            <Box className='flexCenter_Row' gap={"12px"}>
              <Button variant='outlined' onClick={handleClose}>Close</Button>

              <LoadingButton
                variant='contained'
                loading={pulling}
                disabled={!remote}
                startIcon={<ArrowBackIcon />}
                onClick={onPull}>
                Pull here
              </LoadingButton>

              <LoadingButton
                variant='contained'
                loading={pushing}
                disabled={!local}
                endIcon={<ArrowForwardIcon />}
                onClick={onPush}>
                Push to {targetLabel}
              </LoadingButton>
            </Box>
          </Box>
        </Box>
        </Box>
      </Box>
    </Modal>
  )
}

export default TemplateDiffModal;
